/* Get in Touch popup for the corporate pages. Takes over the existing
   "#get-in-touch" links — on the landing page they used to scroll to the form at
   the bottom, on the store page they navigated to the landing page — and opens
   the same lead form in a dialog instead. Without JS the links keep their old
   behaviour, so the page form stays the fallback. */
if (!customElements.get('lead-form-modal')) {
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class LeadFormModal extends HTMLElement {
    connectedCallback() {
      this.opener = null;

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-lead-modal-close]')) this.close();
      });

      /* Two of these on one page would both answer the same links. */
      if (document.querySelector('lead-form-modal') === this) {
        /* Capture phase: the theme runs its own document-level handlers for
           in-page anchors (see collapsible-tabs.js), and section scripts add
           more. Claiming the click on the way down keeps those from scrolling
           the page out from under the popup. */
        document.addEventListener('click', this.onDocumentClick, true);
        this.openIfHashTargetsUs();
      }

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:section:select', (event) => {
          if (event.target.contains(this)) this.open();
        });
        document.addEventListener('shopify:section:deselect', (event) => {
          if (event.target.contains(this)) this.close();
        });
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick, true);
      this.close();
    }

    get triggerSelector() {
      const extra = (this.dataset.extraTriggers || '').trim();
      const base = 'a[href$="' + this.hash + '"], [data-lead-modal-open]';
      return extra ? base + ', ' + extra : base;
    }

    get hash() {
      return this.dataset.hash || '#get-in-touch';
    }

    /* Landing keeps a real #get-in-touch anchor above its page form, so a direct
       link there should still scroll. Only pages without that anchor (the store)
       answer the hash with the popup. */
    openIfHashTargetsUs() {
      if (window.location.hash !== this.hash) return;
      if (document.getElementById(this.hash.slice(1))) return;
      this.open();
    }

    onDocumentClick(event) {
      // Leave cmd/ctrl/shift-click and middle-click to the browser.
      if (event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const trigger = event.target.closest(this.triggerSelector);
      if (!trigger || this.contains(trigger)) return;

      event.preventDefault();
      event.stopPropagation();
      this.open(trigger);
    }

    open(opener) {
      if (!this.hidden) return;

      this.opener = opener || document.activeElement;
      this.removeAttribute('hidden');
      document.documentElement.classList.add('lead-modal-open');
      document.addEventListener('keydown', this.onKeydown);

      /* Straight into the first field — the popup is opened to fill it in. */
      const firstField = this.querySelector('input:not([type="hidden"]), textarea');
      const fallback = this.querySelector('[data-lead-modal-close]');
      const target = firstField || fallback;
      if (target) target.focus();
    }

    close() {
      if (this.hidden) return;

      this.setAttribute('hidden', '');
      document.documentElement.classList.remove('lead-modal-open');
      document.removeEventListener('keydown', this.onKeydown);

      if (this.opener && typeof this.opener.focus === 'function') this.opener.focus();
      this.opener = null;
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }
      if (event.key === 'Tab') this.trapFocus(event);
    }

    trapFocus(event) {
      const items = Array.from(this.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null
      );
      if (!items.length) return;

      const first = items[0];
      const last = items[items.length - 1];

      if (!this.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('lead-form-modal', LeadFormModal);
}
